import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import { RowActionBar } from '@/components/ui/row-action-bar'
import { IconEdit, IconTrash, IconReceipt, IconReceiptOff } from '@tabler/icons-react'
import type { Expense, ExpenseStatus } from '@/api/expenses'

const STATUS_VARIANT: Record<
  ExpenseStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  refacturee: 'default',
  a_refacturer: 'secondary',
  non_facturable: 'outline',
}

function ExpenseStatusBadge({ status }: { status: ExpenseStatus }) {
  const { t } = useTranslation()
  return (
    <Badge variant={STATUS_VARIANT[status]}>
      {t(`exp_status_${status}`)}
    </Badge>
  )
}

const fmtMoney = (v: string, currency = 'MAD') => {
  const num = parseFloat(v)
  if (isNaN(num)) return v
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency,
  }).format(num)
}

const fmtDate = (d: string) => {
  const [y, m, day] = d.split('-')
  return `${day}/${m}/${y}`
}

interface ColumnActions {
  onEdit: (expense: Expense) => void
  onDelete: (expense: Expense) => void
  onBill?: (expense: Expense) => void
  onUnbill?: (expense: Expense) => void
}

export function useExpensesColumns({
  onEdit,
  onDelete,
  onBill,
  onUnbill,
}: ColumnActions): ColumnDef<Expense>[] {
  const { t } = useTranslation()

  return [
    {
      accessorKey: 'expense_date',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('exp_col_date')} />
      ),
      cell: ({ row }) => fmtDate(row.getValue('expense_date')),
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('exp_col_description')} />
      ),
      cell: ({ row }) => (
        <p dir="auto" className="font-medium truncate max-w-[240px]">
          {row.original.description}
        </p>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'case_title',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('exp_col_case')} />
      ),
      cell: ({ row }) =>
        row.original.case_title ? (
          <span dir="auto" className="text-sm">{row.original.case_title}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
      enableSorting: false,
    },
    {
      accessorKey: 'category',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('exp_col_category')} />
      ),
      cell: ({ row }) => (
        <span className="text-sm">{t(`exp_cat_${row.getValue('category')}`)}</span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('exp_col_amount')} />
      ),
      cell: ({ row }) => (
        <span className="font-medium">
          {fmtMoney(row.getValue('amount'))}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('exp_col_status')} />
      ),
      cell: ({ row }) => (
        <ExpenseStatusBadge status={row.getValue('status')} />
      ),
      enableSorting: false,
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const expense = row.original
        const isBilled = expense.status === 'refacturee'
        const canBill = expense.status === 'a_refacturer' && !!expense.case
        return (
          <RowActionBar
            actions={[
              {
                key: 'edit',
                icon: <IconEdit size={16} />,
                label: t('edit'),
                disabled: isBilled,
                onClick: () => onEdit(expense),
              },
              ...(canBill && onBill
                ? [{
                    key: 'bill',
                    icon: <IconReceipt size={16} />,
                    label: t('exp_action_bill'),
                    onClick: () => onBill(expense),
                  }]
                : []),
              ...(isBilled && onUnbill
                ? [{
                    key: 'unbill',
                    icon: <IconReceiptOff size={16} />,
                    label: t('exp_action_unbill'),
                    onClick: () => onUnbill(expense),
                  }]
                : []),
              {
                key: 'delete',
                icon: <IconTrash size={16} />,
                label: t('delete'),
                destructive: true,
                disabled: isBilled,
                onClick: () => onDelete(expense),
              },
            ]}
          />
        )
      },
    },
  ]
}
